/**
 * Shared reader for the 409 pre-flight block that POST /api/payments/refunds
 * (and the support-ticket refund route, which proxies to it) can return
 * BEFORE any money moves — see parkify-pay/api/src/payments/refunds.ts and
 * src/payments/refund-coverage.ts.
 *
 * There are two blocks, and the difference matters to the operator:
 *
 *  - `coverage_gap`        — enforcement exposure: refunding this would leave a
 *                            window this plate is currently paid-and-parked for
 *                            with no coverage. Any refund seat may accept it, so
 *                            the server always sends an `overrideField`.
 *  - `thread_refund_total` — the cumulative per-incident cap (support-ticket
 *                            path only, W1). The server sends an overrideField
 *                            ONLY to a caller holding override.refund_thread_total
 *                            (owner/admin); for anyone else it omits it, and the
 *                            honest answer is "escalate" — never a confirm
 *                            dialog whose "yes" the server would reject anyway.
 *
 * The server is the authority on which of those applies and whether an
 * override is available; this only reads its answer. Anything unrecognised
 * (a real failure, or a 409 with no `message`) falls through to `{kind:'none'}`
 * so the caller's normal `!res.ok` failure path handles it.
 *
 * Extracted out of the support-ticket flow (useTicketActions.ts) so every ops
 * caller of the refund endpoint — Transactions.tsx, the Avery proposal
 * executor, and support tickets — reads the SAME contract instead of each
 * re-parsing (or, worse, re-guessing) the 409 body shape.
 */
export type RefundBlock =
  | { kind: 'none' }
  | { kind: 'escalate'; message: string }
  | { kind: 'confirm'; message: string; overrideField: string }

export async function readRefundBlock(res: Response): Promise<RefundBlock> {
  try {
    const body = await res.clone().json() as {
      error?: string; message?: string
      overrideField?: string; overrideAvailable?: boolean
    }
    if (!body.message) return { kind: 'none' }
    if (body.error !== 'coverage_gap' && body.error !== 'thread_refund_total') return { kind: 'none' }
    if (body.overrideField) {
      return { kind: 'confirm', message: body.message, overrideField: body.overrideField }
    }
    return { kind: 'escalate', message: body.message }
  } catch {
    return { kind: 'none' }
  }
}
